import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Check, X, Palette } from "lucide-react";

import {
  listCityHubPendingReviews,
  reviewCityHubEntries,
  reviewCityHubBranding,
} from "@/lib/city-hub.functions";

export const Route = createFileRoute("/_authenticated/admin/city-hub-queue")({
  head: () => ({
    meta: [
      { title: "City Hub Review Queue — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CityHubQueuePage,
});

function CityHubQueuePage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ["city-hub-queue"],
    queryFn: () => listCityHubPendingReviews(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["city-hub-queue"] });

  const entryMut = useMutation({
    mutationFn: (v: { ids: string[]; action: "approve" | "reject"; note?: string }) =>
      reviewCityHubEntries({ data: { entryIds: v.ids, action: v.action, note: v.note } }),
    onSuccess: (r) => { toast.success(`${r.count} entries ${r.count === 1 ? "" : ""} updated`); invalidate(); setSelected(new Set()); },
    onError: (e: Error) => toast.error(e.message),
  });

  const brandingMut = useMutation({
    mutationFn: (v: { cityId: string; action: "approve" | "reject"; note?: string }) =>
      reviewCityHubBranding({ data: v }),
    onSuccess: () => { toast.success("Branding updated"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (q.isLoading) {
    return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (q.error) {
    return <div className="p-8 text-sm text-rose-600">Failed to load queue: {String(q.error)}</div>;
  }

  const entries = (q.data?.entries ?? []) as any[];
  const branding = (q.data?.branding ?? []) as any[];

  const toggle = (id: string) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <h1 className="text-lg font-semibold">City Hub Review Queue</h1>
          <span className="text-xs text-slate-500 ml-2">
            {entries.length} entries · {branding.length} branding
          </span>
          {selected.size > 0 && (
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => entryMut.mutate({ ids: [...selected], action: "approve" })}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              >
                <Check className="h-4 w-4" /> Approve {selected.size}
              </button>
              <button
                onClick={() => {
                  const note = window.prompt("Reason for rejection (optional):") ?? undefined;
                  entryMut.mutate({ ids: [...selected], action: "reject", note });
                }}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-rose-600 text-white text-sm font-medium hover:bg-rose-700"
              >
                <X className="h-4 w-4" /> Reject {selected.size}
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-8">
        {branding.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5 text-indigo-600" />
              <h2 className="text-base font-semibold">Branding pending review</h2>
            </div>
            <div className="space-y-3">
              {branding.map((b) => (
                <div key={b.city_id} className="rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{b.cities?.name ?? b.city_id}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {b.tagline || <span className="text-slate-400">no tagline</span>}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                      {b.brand_color && <span>Color: <span className="inline-block h-3 w-3 rounded align-middle" style={{ background: b.brand_color }} /> {b.brand_color}</span>}
                      {b.banner_url && <a href={b.banner_url} target="_blank" rel="noreferrer" className="underline">Banner</a>}
                      {b.logo_url && <a href={b.logo_url} target="_blank" rel="noreferrer" className="underline">Logo</a>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => brandingMut.mutate({ cityId: b.city_id, action: "approve" })}
                      className="h-8 px-3 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
                    >Approve</button>
                    <button
                      onClick={() => {
                        const note = window.prompt("Reason:") ?? undefined;
                        brandingMut.mutate({ cityId: b.city_id, action: "reject", note });
                      }}
                      className="h-8 px-3 rounded-md bg-rose-600 text-white text-xs font-medium hover:bg-rose-700"
                    >Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-base font-semibold mb-4">Entries pending review</h2>
          {entries.length === 0 ? (
            <p className="text-sm text-slate-500">Queue is empty.</p>
          ) : (
            <div className="space-y-2">
              {entries.map((e) => (
                <label key={e.id} className="flex gap-3 items-start rounded-xl border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selected.has(e.id)}
                    onChange={() => toggle(e.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {e.cities?.name ?? "?"}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">
                        {e.city_hub_sections?.section_key ?? "section"}
                      </span>
                      <span className="text-xs text-slate-400">{e.kind}</span>
                    </div>
                    <p className="mt-1 font-medium text-sm">{e.title || <span className="text-slate-400">(untitled)</span>}</p>
                    {e.description && <p className="text-xs text-slate-600 line-clamp-2">{e.description}</p>}
                    {e.url && <a href={e.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline">{e.url}</a>}
                  </div>
                </label>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
